import { Injectable, signal, computed } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

export interface Language {
  code: string;
  name: string;
  flag: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly LANG_KEY = 'aforo_language';

  languages: Language[] = [
    {
      code: 'en',
      name: 'English',
      flag: '🇬🇧'
    },
    {
      code: 'es',
      name: 'Español',
      flag: '🇪🇸'
    },
    {
      code: 'fr',
      name: 'Français',
      flag: '🇫🇷'
    },
    {
      code: 'de',
      name: 'Deutsch',
      flag: '🇩🇪'
    },
    {
      code: 'it',
      name: 'Italiano',
      flag: '🇮🇹'
    }
  ];

  private normalizeLanguageCode(code: string | null | undefined): string {
    const candidate = String(code || '').trim().toLowerCase();
    const supported = this.languages.find(lang => lang.code === candidate);
    return supported ? supported.code : this.getDefaultLanguage();
  }

  // NEW: Primary signal-based state (initialized after languages array)
  private readonly currentLangSignal = signal<string>(
    this.normalizeLanguageCode(localStorage.getItem(this.LANG_KEY))
  );

  // NEW: Public readonly signal
  public readonly currentLanguage = this.currentLangSignal.asReadonly();

  // NEW: Computed signal for language info
  public readonly currentLanguageInfo = computed(() => {
    const langCode = this.currentLanguage();
    return this.languages.find(lang => lang.code === langCode) || this.languages[0];
  });

  // BACKWARD COMPATIBILITY: Keep observable for gradual migration
  private currentLangSubject = new BehaviorSubject<string>(
    this.normalizeLanguageCode(localStorage.getItem(this.LANG_KEY))
  );

  currentLang$ = this.currentLangSubject.asObservable();

  constructor(private translate: TranslateService) {
    this.initializeLanguage();
  }

  private initializeLanguage() {
    const currentLang = this.currentLanguage();
    this.translate.setDefaultLang('en');
    this.translate.use(currentLang);
    localStorage.setItem(this.LANG_KEY, currentLang);
  }

  async setLanguage(langCode: string): Promise<void> {
    try {
      const normalizedLangCode = this.normalizeLanguageCode(langCode);

      // Update both signal and observable for compatibility
      this.currentLangSignal.set(normalizedLangCode);
      this.currentLangSubject.next(normalizedLangCode);

      // Save to local storage
      localStorage.setItem(this.LANG_KEY, normalizedLangCode);

      // Update translation service
      await this.translate.use(normalizedLangCode).toPromise();

      console.log(`Language changed to: ${normalizedLangCode}`);
    } catch (error) {
      console.error('Error setting language:', error);
      throw error;
    }
  }

  getCurrentLanguage(): string {
    return this.currentLanguage();
  }

  getCurrentLanguageInfo(): Language {
    return this.currentLanguageInfo();
  }

  private getDefaultLanguage(): string {
    // Try to get browser language
    const browserLang = navigator.language.split('-')[0];

    // Check if we support this language
    const supportedLang = this.languages.find(lang => lang.code === browserLang);

    return supportedLang ? browserLang : 'en';
  }
} 